import { useAuth } from '@/lib/AuthContext';
import { APP_STORE_URL, GET_APP_POPUP_SHOWN_KEY, isIosBrowser } from '@/lib/appStore';
import { mixpanelService } from '@/services/mixpanel.service';
import { useCallback, useEffect, useState } from 'react';
import { Platform } from 'react-native';

// Let the page settle before sliding the card in so it doesn't compete with
// the initial content paint.
const SHOW_DELAY_MS = 1500;

export interface GetAppPopup {
  /** Close the card (X). */
  dismiss: () => void;
  /** Open the App Store listing and close the card. */
  openStore: () => void;
}

function alreadyShown(): boolean {
  try {
    return window.localStorage.getItem(GET_APP_POPUP_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

function markShown(): void {
  try {
    window.localStorage.setItem(GET_APP_POPUP_SHOWN_KEY, '1');
  } catch {
    // Storage blocked (private mode etc.): it just shows again next visit.
  }
}

/**
 * Decides whether the "Get the Open Studio app" card should slide up on this
 * page load, or `null` when it should not. Rules:
 *   - web only, in an iPhone/iPad browser (there is no Android listing yet)
 *   - not when already running as an installed home-screen web app
 *   - shown once per browser, ever: the flag is written the moment the card
 *     appears, so clicking, closing or simply reloading all count as "seen"
 */
export function useGetAppPopup(): GetAppPopup | null {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    if (nav.standalone) return;
    if (!isIosBrowser({
      userAgent: nav.userAgent,
      platform: nav.platform,
      maxTouchPoints: nav.maxTouchPoints,
    })) return;
    if (alreadyShown()) return;

    const id = setTimeout(() => {
      markShown();
      setVisible(true);
      void mixpanelService.track('Get App Popup Shown', {}, user);
    }, SHOW_DELAY_MS);
    return () => clearTimeout(id);
    // Runs once per mount; the user is only read for event attribution.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    void mixpanelService.track('Get App Popup Dismissed', {}, user);
  }, [user]);

  const openStore = useCallback(() => {
    setVisible(false);
    void mixpanelService.track('Get App Popup Clicked', { url: APP_STORE_URL }, user);
    window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
  }, [user]);

  if (!visible) return null;
  return { dismiss, openStore };
}
